import { FollowButtonOutline, FollowButtonVariant } from "@product/scmp-sdk";
import { useToggle } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { isValidUrl } from "scmp-app/lib/utils";
import type { authorHeaderAuthor$key } from "scmp-app/queries/__generated__/authorHeaderAuthor.graphql";

import {
  Avatar,
  BasicInformationSubGrid,
  Bio,
  Container,
  DownTriangle,
  Education,
  Expertise,
  ExtraInformationItemLabel,
  Follow,
  Language,
  Location,
  LocationAndSocialSubGrid,
  MoreButton,
  Name,
  Role,
  SocialLinks,
  TwitterLink,
  UpTriangle,
} from "./styles";

type Props = {
  reference: authorHeaderAuthor$key;
};

export const AuthorHeader: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const author = useFragment(
    graphql`
      fragment authorHeaderAuthor on Author {
        name
        location
        socialLinks {
          class
          url
        }
        role {
          text
        }
        bio
        education
        areaOfExpertise
        languageSpoken
        ...entityFollowButtonBase
        ...authorAvatarAuthor
        ...authorSocialLinksAuthor
      }
    `,
    reference_,
  );

  const renderFollowButton = () => (
    <Follow
      outline={FollowButtonOutline.Outlined}
      reference={author}
      source="Author"
      variant={FollowButtonVariant.Light}
    />
  );

  const renderTwitterLink = () => {
    const twitterLink = author.socialLinks?.find(item => item?.class === "twitter")?.url;
    if (!twitterLink || !isValidUrl(twitterLink)) return;

    const twitterName = `@${new URL(twitterLink).pathname.slice(1)}`;
    return (
      <TwitterLink href={twitterLink} target="_blank">
        {twitterName}
      </TwitterLink>
    );
  };

  const [isShowMore, toggleShowMore] = useToggle(false);

  const renderBio = () => {
    if (!author.bio) return;
    const delimitedBio = author.bio.split(" ");

    const needTrim = delimitedBio.length > 120;
    const shouldTrim = !isShowMore && needTrim;
    const trimmedBio = `${author.bio.split(" ").slice(0, 120).join(" ")}...`;

    return {
      component: <Bio>{shouldTrim ? trimmedBio : author.bio}</Bio>,
      needTrim,
    };
  };
  const bio = renderBio();

  const renderShowMoreButton = () => {
    const hasMore =
      !!bio?.needTrim || !!author.areaOfExpertise?.length || !!author.languageSpoken?.length;
    if (!hasMore) return null;

    return (
      <MoreButton onClick={toggleShowMore} text>
        {isShowMore ? (
          <>
            Show less <UpTriangle />
          </>
        ) : (
          <>
            Show more <DownTriangle />
          </>
        )}
      </MoreButton>
    );
  };

  return (
    <Container>
      <Avatar height={320} reference={author} width={320} withOutline withoutLink />
      <Name>{author.name}</Name>
      {renderFollowButton()}
      <LocationAndSocialSubGrid>
        {author.location && <Location> {author.location}</Location>}
        {renderTwitterLink()}
        <SocialLinks reference={author} />
      </LocationAndSocialSubGrid>
      <BasicInformationSubGrid>
        {author.role?.text && <Role>{author.role.text}</Role>}
        {bio?.component}
      </BasicInformationSubGrid>
      {isShowMore && (
        <>
          {author.education && (
            <Education>
              <ExtraInformationItemLabel>Education:</ExtraInformationItemLabel>
              {author.education}
            </Education>
          )}
          {!!author.areaOfExpertise?.length && (
            <Expertise>
              <ExtraInformationItemLabel>Areas of Expertise:</ExtraInformationItemLabel>
              {author.areaOfExpertise.join(", ")}
            </Expertise>
          )}
          {!!author.languageSpoken?.length && (
            <Language>
              <ExtraInformationItemLabel>Languages Spoken:</ExtraInformationItemLabel>{" "}
              {author.languageSpoken.join(", ")}
            </Language>
          )}
        </>
      )}

      {renderShowMoreButton()}
    </Container>
  );
};

AuthorHeader.displayName = "AuthorHeader";
